import _ from "underscore";
import { stringify } from "qs";

// The following function takes care of the SEO changes that need to
// happen when we paginate - i.e.:
// 1. Change the title and description tags to read "Page ${newPage} of ${totalPages}"
//    except for on page 1, where it should be deleted
// 2. Update the "prev" link tag in the header to be the previous page
//    except for on page 1, where it should be deleted
// 3. Update the canonical link to be the current page
// 4. Update the "next" link in the header to be the new next page
//    except for on page n, where it should be deleted
export function paginateHeadContent(oldPage, newPage, totalPages, queryParams) {
  updatePreviousLink(oldPage, newPage, queryParams);
  updateNextLink(oldPage, newPage, totalPages, queryParams);

  document.title = getNewTitle(newPage, totalPages);
  document.querySelector("meta[name='description']")?.setAttribute("content", getNewDescription(newPage, totalPages));
}

export function isFirstPage(page) {
  return page === 1;
}

export function removePaginationFromLink(link, queryParams) {
  if (!link) {
    return;
  }

  // Then we omit from that object the page
  const currentQueryWithoutPage = _.omit(queryParams, "page");

  // Get the base of the link we passed in
  const currentLinkBase = link.split("?")[0];

  // Construct a new link based on the query param object. If it's empty, we just want the base.
  // If the query is *not* empty, we just return the params
  const newLinkWithoutPagination = _.isEmpty(currentQueryWithoutPage)
    ? currentLinkBase
    : `${currentLinkBase}?${stringify(currentQueryWithoutPage)}`;

  return newLinkWithoutPagination;
}

export function updatePaginationInLink(link, newPageNumber, queryParams) {
  if (!link) {
    return;
  }

  // Then we omit from that object the page
  const currentQueryWithoutPage = _.omit(queryParams, "page");

  // Get the base of the link we passed in
  const currentLinkBase = link.split("?")[0];

  // Construct a new link based on the query param object. If it's empty, we just want the base with pagination.
  // If the query is *not* empty, we add the pagination onto the end
  let newLinkWithPagination;
  if (_.isEmpty(currentQueryWithoutPage)) {
    newLinkWithPagination = `${currentLinkBase}?page=${newPageNumber}`;
  } else {
    newLinkWithPagination = `${currentLinkBase}?${stringify(currentQueryWithoutPage)}&page=${newPageNumber}`;
  }

  return newLinkWithPagination;
}

export function updatePreviousLink(oldPage, newPage, queryParams) {
  if (isFirstPage(newPage)) {
    const previousLink = document.querySelector("link[rel='prev']");
    if (!previousLink) {
      return;
    }
    previousLink.remove();
    return;
  }

  if (isFirstPage(oldPage) && !document.querySelector("link[rel='prev']")) {
    const currentCanonicalLink = document.querySelector("link[rel='canonical']");
    if (!currentCanonicalLink) {
      return;
    }

    const previousLink = updatePaginationInLink(currentCanonicalLink.getAttribute("href"), "1", queryParams);
    document.querySelector("head").append(`<link rel='prev' href=${previousLink}>`);
    return;
  }

  const currentPrevLink = document.querySelector("link[rel='prev']");
  if (!currentPrevLink) {
    return;
  }
  currentPrevLink.setAttribute(
    "href",
    updatePaginationInLink(currentPrevLink.getAttribute("href"), newPage - 1, queryParams)
  );
}

export function updateNextLink(oldPage, newPage, totalPages, queryParams) {
  if (newPage === totalPages) {
    // We are on the last page, remove the "next" link
    const currentNextLink = document.querySelector("link[rel='next']");
    if (!currentNextLink) {
      return;
    }
    currentNextLink.remove();
    return;
  }

  const currentCanonicalLink = document.querySelector("link[rel='canonical']");
  const currentNextLink = document.querySelector("link[rel='next']");
  if (!currentNextLink || !currentCanonicalLink) {
    return;
  }
  // If our oldPage is the same as the totalPages, we are coming back from the last page.
  // Therefore, we need to add back in the next link.
  if (oldPage === totalPages || !currentNextLink) {
    document
      .querySelector("head")
      .append(
        `<link rel='next' href=${updatePaginationInLink(
          currentCanonicalLink.getAttribute("href"),
          newPage + 1,
          queryParams
        )}>`
      );
    return;
  }

  currentNextLink.setAttribute(
    "href",
    updatePaginationInLink(currentCanonicalLink.getAttribute("href"), newPage + 1, queryParams)
  );
}

export function getNewTitle(newPage, totalPages) {
  const currentTitle = document.title;

  // If on a page other than page one, the title of the page will start with e.g. Page 2 of 10 |
  // If it does, we can split the title on the pipe and then remove the first item in the array
  // (i.e. the pagination bit).
  const pageTitleWithoutPagination = currentTitle.startsWith("Page")
    ? currentTitle.split(`${totalPages} - `).slice(1)
    : currentTitle;

  if (isFirstPage(newPage)) {
    return pageTitleWithoutPagination;
  }

  const pageText = `Page ${newPage} of ${totalPages} - `;

  return `${pageText}${pageTitleWithoutPagination}`;
}

export function getNewDescription(newPage, totalPages) {
  const currentDescription = document.querySelector("meta[name='description']").getAttribute("content");
  // If the current title starts with "Page", we need to split the description of the page by the end of the first sentence
  const pageDescriptionWithoutPagination = currentDescription?.startsWith("Page")
    ? currentDescription.split("- ").slice(1).join("- ")
    : currentDescription;

  if (isFirstPage(newPage)) {
    return pageDescriptionWithoutPagination;
  }

  const pageDescription = `Page ${newPage} of ${totalPages} - `;

  return `${pageDescription}${pageDescriptionWithoutPagination}`;
}
